import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FilterControlTypes, FilterListItem, FilterTypes, RiskFilterType } from '../../../models/risk-filter-type';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';

@Component({
  selector: 'lib-risk-filter',
  templateUrl: './risk-filter.component.html',
  styleUrls: ['./risk-filter.component.scss']
})

export class RiskFilterComponent implements OnChanges {

  @Input() filterTypes: Array<FilterListItem>;
  @Input() disabled: boolean;
  @Output() applyFilter: EventEmitter<Array<RiskFilterType>> = new EventEmitter<Array<RiskFilterType>>();

  public selectedFilter: FilterListItem;
  public filtersArr: Array<RiskFilterType> = [];

  private timeoutId;

  private allAvailableFilters: Array<RiskFilterType> = [
    new RiskFilterType(FilterTypes.name, FilterControlTypes.inputWithOptions, 'Name'),
    new RiskFilterType(FilterTypes.lastName, FilterControlTypes.inputWithOptions, 'Last Name'),
    new RiskFilterType(FilterTypes.firstName, FilterControlTypes.inputWithOptions, 'First Name'),
    new RiskFilterType(FilterTypes.userDepartment, FilterControlTypes.multiselect, 'Department'),
    new RiskFilterType(FilterTypes.userLocation, FilterControlTypes.multiselect, 'Location'),
    new RiskFilterType(FilterTypes.userRole, FilterControlTypes.multiselect, 'Role'),
    new RiskFilterType(FilterTypes.userManagerRole, FilterControlTypes.multiselect, 'Role'),
    new RiskFilterType(FilterTypes.departmentByName, FilterControlTypes.inputWithOptions, 'Department by Name'),
    new RiskFilterType(FilterTypes.locationByName, FilterControlTypes.inputWithOptions, 'Location by Name'),
    new RiskFilterType(FilterTypes.userId, FilterControlTypes.input, 'User Id'),
    new RiskFilterType(FilterTypes.employeeId, FilterControlTypes.input, 'Employee Id'),
    new RiskFilterType(FilterTypes.email, FilterControlTypes.input, 'Email'),
    new RiskFilterType(FilterTypes.login, FilterControlTypes.input, 'Login'),
    new RiskFilterType(FilterTypes.userTitle, FilterControlTypes.inputWithOptions, 'Title'),
    new RiskFilterType(FilterTypes.userTeam, FilterControlTypes.multiselect, 'Team'),
    new RiskFilterType(FilterTypes.category, FilterControlTypes.inputWithOptions, 'Category'),
    new RiskFilterType(FilterTypes.userStatus, FilterControlTypes.multiselect, 'Status'),
    new RiskFilterType(FilterTypes.ansTypeUser, FilterControlTypes.multiselect, 'ANS'),
    new RiskFilterType(FilterTypes.ansTypeVendor, FilterControlTypes.multiselect, 'ANS'),
    new RiskFilterType(FilterTypes.userRPXMobile, FilterControlTypes.multiselect, 'RPX Mobile'),
    new RiskFilterType(FilterTypes.departmentId, FilterControlTypes.inputWithOptions, 'Department id'),
    new RiskFilterType(FilterTypes.parentDepartment, FilterControlTypes.inputWithOptions, 'Parent Department'),
    new RiskFilterType(FilterTypes.parentResource, FilterControlTypes.inputWithOptions, 'Parent Resource'),
    new RiskFilterType(FilterTypes.plan, FilterControlTypes.inputWithOptions, 'Plan'),
    new RiskFilterType(FilterTypes.function, FilterControlTypes.inputWithOptions, 'Function'),
    new RiskFilterType(FilterTypes.resourceId, FilterControlTypes.inputWithOptions, 'Resource id'),
    new RiskFilterType(FilterTypes.resource, FilterControlTypes.inputWithOptions, 'Resource'),
    new RiskFilterType(FilterTypes.siteType, FilterControlTypes.multiselect, 'Site Type'),
    new RiskFilterType(FilterTypes.siteId, FilterControlTypes.inputWithOptions, 'Site id'),
    new RiskFilterType(FilterTypes.siteRegion, FilterControlTypes.inputWithOptions, 'Region'),
    new RiskFilterType(FilterTypes.siteState, FilterControlTypes.inputWithOptions, 'State'),
    new RiskFilterType(FilterTypes.siteCountry, FilterControlTypes.inputWithOptions, 'Country'),
    new RiskFilterType(FilterTypes.siteCity, FilterControlTypes.inputWithOptions, 'City'),
    new RiskFilterType(FilterTypes.contractExpiresAfter, FilterControlTypes.date, 'Contract Expires After'),
    new RiskFilterType(FilterTypes.contractExpiresBefore, FilterControlTypes.date, 'Contract Expires Before'),
    new RiskFilterType(FilterTypes.contractName, FilterControlTypes.inputWithOptions, 'Contract Name'),
    new RiskFilterType(FilterTypes.contactName, FilterControlTypes.inputWithOptions, 'Contact Name'),
    new RiskFilterType(FilterTypes.contactEmail, FilterControlTypes.inputWithOptions, 'Contact Email'),
    new RiskFilterType(FilterTypes.uniqueId, FilterControlTypes.inputWithOptions, 'Unique id')
  ];

  constructor(private translateService: TranslateService) {
  }

  ngOnChanges(changes: SimpleChanges) {
    // reset selected filters on new filters array pushed from host component
    if (changes.hasOwnProperty('filterTypes')) {
      this.filterTypes = _.cloneDeep(this.filterTypes.filter(ft => this.allAvailableFilters.findIndex(af => af.id === ft.filterTypeId) >= 0));
      this.filterTypes.forEach(ft => ft.label = this.translateService.instant(`filter.filterTypeLabels.${ft.label}`));
      this.filtersArr = [];

      /**
       * Set Default Visible Fields
       */
      this.filterTypes.forEach(filter => {
        if (filter.visible) {
          this.onNewFilterSelected(filter.filterTypeId);
        }
      });
    }
  }

  onNewFilterSelected(selectedFilterId): void {

    let filter = this.allAvailableFilters.find(af => af.id === selectedFilterId);
    if (!filter) {
      return;
    }

    filter = _.cloneDeep(filter);

    const filterTypeIndex = this.filterTypes.findIndex(ft => ft.filterTypeId === filter.id);

    if (filterTypeIndex >= 0) {
      const initializedFilter = this.filterTypes.splice(filterTypeIndex, 1);
      if (initializedFilter?.length > 0) {
        filter.index = initializedFilter[0].index;
        filter.label = initializedFilter[0].label;
      }
    }

    this.filtersArr.push(filter);

    this.sortFiltersDropdown();
  }

  onHideFilter(filterTypeId: FilterTypes): void {
    const filterIndex = this.filtersArr.findIndex(fa => fa.id === filterTypeId);
    if (filterIndex < 0) {
      return;
    }
    const deletedFilters = this.filtersArr.splice(filterIndex, 1);
    if (!deletedFilters ||
      deletedFilters.length === 0) {
      return;
    }

    const deletedFilter = deletedFilters[0];
    const filterType = this.filterTypes.find(ft => ft.filterTypeId === deletedFilter.id);

    if (!filterType) {
      this.filterTypes.push(new FilterListItem(deletedFilter.index, deletedFilter.label, deletedFilter.id));
    }

    this.sortFiltersDropdown();
    this.onFilterHidden();
  }

  onFilterHidden() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
    this.timeoutId = setTimeout(this.onApplyFilter.bind(this), 1000);
  }

  sortFiltersDropdown(): void {
    const filterTypes = this.filterTypes.map(ft => ft);
    filterTypes.sort((a, b) => a.index < b.index ? -1 : a.index > b.index ? 1 : 0);
    this.filterTypes = filterTypes;
    this.selectedFilter = new FilterListItem(-1, '', FilterTypes.none);
  }

  onFilterValueChanged(event: RiskFilterType): void {
    const filterTypeIndex = this.filtersArr.findIndex(f => f.id === event.id);
    if (filterTypeIndex < 0) {
      return;
    }

    this.filtersArr[filterTypeIndex].value = event.value;
    this.filtersArr[filterTypeIndex].filterOption = event.filterOption;
  }

  onApplyFilter(): void {
    this.applyFilter.emit(
      this.filtersArr.filter(f => {
        return (f.value instanceof Date) ? (f.value as Date) : f.value?.length;
      })
    );
  }
}

